import React, { createContext, useContext, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'

import { useAuth } from './AuthContext'
import { useSelectedOrganization } from './OrganizationContext'
import { checkAccessForOrganization, listTenants } from '../services/AuthzService'

/**
 * Loads the authz tenant/organization list (GET /api/bff/tenants) and probes, once per
 * organization, whether it has mia-flow product access (GET /api/access). Mirrors
 * ai-foundry-website/src/lib/TenantAccessContext.tsx.
 */
const TenantAccessContext = createContext({
  accessByOrganization: {},
  error: null,
  hasAccess: false,
  loading: true,
  tenants: [],
})

export function TenantAccessProvider({ children }) {
  const { isAuthenticated } = useAuth()
  const { selectedOrganization, setSelectedOrganization } = useSelectedOrganization()
  const [tenants, setTenants] = useState([])
  const [tenantsLoading, setTenantsLoading] = useState(true)
  const [error, setError] = useState(null)
  // Access results are stored together with the exact `tenants` array they were probed for, so
  // `accessLoading` below is DERIVED from render state instead of being its own state set inside
  // the probe effect. With separate state, the auto-seed effect further down ran in the same
  // commit as the probe effect and read a stale `accessLoading === false` / empty access map,
  // making it overwrite the persisted selection with tenants[0] on every page load.
  const [access, setAccess] = useState({ forTenants: null, byOrganization: {} })
  const accessByOrganization = access.forTenants === tenants ? access.byOrganization : {}
  const accessLoading = access.forTenants !== tenants

  useEffect(() => {
    if (!isAuthenticated) { return undefined }
    let cancelled = false
    const load = async () => {
      setTenantsLoading(true)
      try {
        const data = await listTenants()
        if (!cancelled) { setTenants(data) }
      } catch (err) {
        if (!cancelled) { setError(err instanceof Error ? err : new Error(String(err))) }
      } finally {
        if (!cancelled) { setTenantsLoading(false) }
      }
    }
    void load()
    return () => { cancelled = true }
  }, [isAuthenticated])

  // One access probe per organization (access is an org-level, not tenant-level, concern).
  useEffect(() => {
    let cancelled = false
    const tenantByOrganization = new Map()
    tenants.forEach((tenant) => {
      if (!tenantByOrganization.has(tenant.organization)) {
        tenantByOrganization.set(tenant.organization, tenant.id)
      }
    })
    const entries = [...tenantByOrganization.entries()]

    if (entries.length === 0) {
      setAccess({ forTenants: tenants, byOrganization: {} })
      return undefined
    }

    const load = async () => {
      const results = await Promise.all(
        entries.map(([organization, tenant]) => checkAccessForOrganization(organization, tenant)),
      )
      if (cancelled) { return }
      setAccess({
        forTenants: tenants,
        byOrganization: Object.fromEntries(
          entries.map(([organization], index) => [organization, results[index] ?? false]),
        ),
      })
    }
    void load()
    return () => { cancelled = true }
  }, [tenants])

  // Auto-seed / auto-correct the selected authz org: pick the first accessible tenant when
  // nothing is selected (or the current selection is gone / lost access), so x-mia-acl-context is
  // populated without requiring a UI selector. It must never override a selection the user made
  // through OrganizationSelector — that one is persisted and has to survive a reload.
  useEffect(() => {
    if (tenantsLoading || accessLoading) { return }
    if (tenants.length === 0) { return }

    const selected = selectedOrganization
      ? tenants.find((t) => t.id === selectedOrganization.tenant
        && t.organization === selectedOrganization.organization)
      : undefined

    if (selected) {
      // Keep the persisted choice, including when its organization currently probes as
      // no-access — only move away if some OTHER organization does have access. This is also
      // what prevents the loop when nothing has access (e.g. /api/access 403): re-selecting
      // tenants[0] as a fresh object would re-trigger this effect forever.
      if (accessByOrganization[selected.organization]) { return }
      const accessible = tenants.find((t) => accessByOrganization[t.organization])
      if (!accessible) { return }
      setSelectedOrganization({
        organization: accessible.organization,
        tenant: accessible.id,
        tenantName: accessible.title,
      })
      return
    }

    // Nothing selected yet, or the selected tenant is no longer listed.
    const pick = tenants.find((t) => accessByOrganization[t.organization]) || tenants[0]
    setSelectedOrganization({ organization: pick.organization, tenant: pick.id, tenantName: pick.title })
  }, [tenants, accessByOrganization, tenantsLoading, accessLoading, selectedOrganization, setSelectedOrganization])

  const hasAccess = useMemo(
    () => Object.values(accessByOrganization).some(Boolean),
    [accessByOrganization],
  )

  const value = useMemo(() => ({
    accessByOrganization,
    error,
    hasAccess,
    loading: tenantsLoading || accessLoading,
    tenants,
  }), [accessByOrganization, accessLoading, error, hasAccess, tenants, tenantsLoading])

  return (
    <TenantAccessContext.Provider value={value}>
      {children}
    </TenantAccessContext.Provider>
  )
}

TenantAccessProvider.propTypes = {
  children: PropTypes.node.isRequired,
}

export function useTenantAccess() {
  return useContext(TenantAccessContext)
}
